/**
 * Estimated labor, in whole minutes.
 *
 * Minutes because that is what a person standing under a tree thinks in. This
 * replaced decimal hours, which were stored as exact decimal strings and had to
 * print "3.75 = 3 hours 45 minutes" underneath the input so a misplaced decimal
 * point could be caught before it reached a quote. That read-back existed only
 * because the unit was wrong — nobody schedules 3.75 of anything, and 3.75 and
 * 3.45 look identical on a page until they are spelled out.
 *
 * Integers also retire a whole class of arithmetic problem. Decimal hours had
 * to be summed in hundredths and carried as strings end to end so that adding
 * ninety of them gave the same answer every time; ninety integers cannot drift,
 * so a plain `number` is both correct and honest here.
 */

const MINUTES_PER_HOUR = 60

/**
 * The largest estimate that is a real answer rather than a typo.
 *
 * Sixteen hours: past a single crew's long day on one tree, and the point at
 * which a number is far more likely to be minutes typed where hours were meant
 * — or an extra digit — than an actual plan.
 */
export const MAX_ESTIMATED_MINUTES = 16 * MINUTES_PER_HOUR

/**
 * Parse an estimate typed by a person, or null if it is not one.
 *
 * Deliberately strict rather than `Number.parseInt`, which accepts "45abc" and
 * "45.9" (silently truncating). A value that does not round-trip is a mistake
 * upstream, and coercing it quietly would put a wrong number into a quote.
 */
export function parseMinutes(value: string | null): number | null {
  if (value === null) return null
  const trimmed = value.trim()
  if (!/^\d{1,4}$/.test(trimmed)) return null
  const parsed = Number(trimmed)
  return parsed > MAX_ESTIMATED_MINUTES ? null : parsed
}

/**
 * Sum estimates, ignoring anything not yet estimated.
 *
 * An unestimated tree contributes nothing rather than zero — the two mean
 * different things, and the caller is expected to say which total it has by
 * counting the nulls itself.
 */
export function sumMinutes(values: (number | null)[]): number {
  return values.reduce<number>(
    (running, value) => (value === null ? running : running + value),
    0,
  )
}

/**
 * Compact, for a map pin: `"45m"`, `"2h"`, `"3h 45m"`.
 *
 * Short because ninety of these sit on one screen and the label is beside a
 * tree number, not standing alone. A whole number of hours drops the minutes
 * entirely — "2h 0m" is three characters spent saying nothing.
 */
export function formatMinutesShort(value: number | null): string | null {
  if (value === null) return null
  const hours = Math.floor(value / MINUTES_PER_HOUR)
  const minutes = value % MINUTES_PER_HOUR
  if (hours === 0) return `${String(minutes)}m`
  if (minutes === 0) return `${String(hours)}h`
  return `${String(hours)}h ${String(minutes)}m`
}

/**
 * Spelled out, for a form or a document: `"3 hours 45 minutes"`.
 *
 * Used where there is room to read it and where the reader is checking the
 * number rather than scanning past it — under the input as it is typed, and in
 * a proposal a client reads.
 */
export function formatMinutesLong(value: number | null): string | null {
  if (value === null) return null

  const hours = Math.floor(value / MINUTES_PER_HOUR)
  const minutes = value % MINUTES_PER_HOUR

  const parts: string[] = []
  if (hours > 0) {
    parts.push(`${String(hours)} ${hours === 1 ? 'hour' : 'hours'}`)
  }
  // Minutes show when there are some, and when there is nothing else to say —
  // "0" should read as "0 minutes" rather than as an empty line.
  if (minutes > 0 || hours === 0) {
    parts.push(`${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}`)
  }
  return parts.join(' ')
}

/**
 * Minutes as decimal hours, for a figure that is summed across a whole site:
 * `"11.25"`.
 *
 * A site total of 675 minutes is technically correct and useless — nobody
 * plans crew time in three-digit minutes. Hours are the unit at that scale, so
 * this is the one place the two meet: minutes going in, hours coming out, with
 * the conversion in exactly one function rather than at every call site.
 */
export function minutesAsHours(value: number): string {
  return (value / MINUTES_PER_HOUR).toFixed(2)
}
